// AddToListModal.js
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch, faPlus } from '@fortawesome/free-solid-svg-icons';
import NewListModal from './NewListModal'; // Import NewListModal

function AddToListModal({ isOpen, onClose, cocktail, lists, apiClient, userData, onSave, onUpdateLists }) {
  const [userLists, setUserLists] = useState([]);
  const [selectedLists, setSelectedLists] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isNewListModalOpen, setIsNewListModalOpen] = useState(false); // State for NewListModal
  const IMAGE_BASE_URL = '/img/cocktails/';
  const LIST_IMAGE_BASE_URL = '/img/cocktails/';

  useEffect(() => {
    const fetchLists = async () => {
      try {
        setLoading(true);
        const data = await apiClient.request(`/api/lists?userid=${userData.userid}`, {
          method: 'GET',
        });
        setUserLists(data || []);
      } catch (err) {
        setError(err.message || 'Failed to fetch lists');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      if (lists && lists.length > 0) {
        setUserLists(lists);
      } else {
        fetchLists();
      }

      const initialSelected = new Set();
      if (cocktail.in_lists && cocktail.in_lists.length > 0) {
        cocktail.in_lists.forEach((listId) => initialSelected.add(listId));
      }
      setSelectedLists(initialSelected);
    }
  }, [isOpen, lists, cocktail, apiClient, userData]);

  const handleCheckboxChange = (listId) => {
    const newSelected = new Set(selectedLists);
    if (newSelected.has(listId)) {
      newSelected.delete(listId);
    } else {
      newSelected.add(listId);
    }
    setSelectedLists(newSelected);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      const listsToAdd = [];
      const listsToRemove = [];

      userLists.forEach((list) => {
        const listId = list.listid;
        const isSelected = selectedLists.has(listId);
        const wasInList = cocktail.in_lists && cocktail.in_lists.includes(listId);

        if (isSelected && !wasInList) {
          listsToAdd.push(listId);
        } else if (!isSelected && wasInList) {
          listsToRemove.push(listId);
        }
      });

      for (const listId of listsToAdd) {
        await apiClient.request('/api/lists_drinks_items/add', {
          method: 'POST',
          body: JSON.stringify({
            listid: listId,
            drinkid: cocktail.drinkid,
          }),
        });
      }

      for (const listId of listsToRemove) {
        await apiClient.request('/api/lists_drinks_items/del', {
          method: 'DELETE',
          body: JSON.stringify({
            listid: listId,
            drinkid: cocktail.drinkid,
          }),
        });
      }

      const updatedLists = userLists.map((list) => {
        const listId = list.listid;
        let newCount = list.cocktails || 0;
        if (listsToAdd.includes(listId)) {
          newCount += 1;
        } else if (listsToRemove.includes(listId)) {
          newCount = Math.max(0, newCount - 1);
        }
        return { ...list, cocktails: newCount };
      });

      onUpdateLists(updatedLists);

      const updatedInLists = userLists
        .filter((list) => selectedLists.has(list.listid))
        .map((list) => list.listid);

      onSave(cocktail.drinkid, updatedInLists);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update lists');
      if (err.message === 'Failed to refresh token') {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewList = () => {
    setIsNewListModalOpen(true); // Open NewListModal
  };

  const handleCloseNewListModal = () => {
    setIsNewListModalOpen(false); // Close NewListModal
  };

  const handleCreateList = (newList) => {
    // Add the new list to userLists
    const updatedUserLists = [...userLists, { ...newList, cocktails: 0, pictures: [] }];
    setUserLists(updatedUserLists);
    // Update the lists in App.js
    onUpdateLists(updatedUserLists);
    // Automatically check the new list
    setSelectedLists((prev) => new Set(prev).add(newList.listid));
    // Close the NewListModal
    setIsNewListModalOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="add-to-list-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="add-to-list-modal">
        <div className="add-to-list-modal-header">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-lg overflow-hidden mr-3">
              <img
                src={cocktail.picture ? `${IMAGE_BASE_URL}${cocktail.picture}` : '/img/cocktails/1.png'}
                className="w-full h-full object-cover"
                alt={cocktail.name}
              />
            </div>
            <h3 className="text-lg font-semibold">Save {cocktail.name}</h3>
          </div>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} className="text-gray-400" />
          </button>
        </div>

        <div className="add-to-list-modal-search">
          <div className="add-to-list-modal-search-inner">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400 mr-2 text-sm" />
            <input
              type="text"
              placeholder="Search your lists..."
              className="add-to-list-modal-search-input"
            />
          </div>
        </div>

        <div className="add-to-list-modal-list-container">
          <h4 className="text-sm font-medium text-gray-400 mb-3">Your Lists</h4>
          {loading ? (
            <p className="text-gray-400">Loading lists...</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : userLists.length === 0 ? (
            <p className="text-gray-400">No lists found.</p>
          ) : (
            <ul className="space-y-2 mb-4">
              {userLists.map((list) => (
                <li key={list.listid} className="add-to-list-items">
                  <div className="flex items-center">
                    <div className="add-to-list-image-div">
                      <img
                        src={
                          list.pictures && list.pictures.length > 0 && list.pictures[0] && list.pictures[0] !== 'null'
                            ? `${LIST_IMAGE_BASE_URL}${list.pictures[0]}`
                            : '/img/cocktails/1.png'
                        }
                        className="w-full h-full object-cover"
                        alt={list.listname}
                      />
                    </div>
                    <div>
                      <h5 className="font-medium">{list.listname}</h5>
                      <p className="text-xs text-gray-400">{list.cocktails || 0} cocktails</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedLists.has(list.listid)}
                    onChange={() => handleCheckboxChange(list.listid)}
                    className="add-to-list-checkbox"
                  />
                </li>
              ))}
            </ul>
          )}

          <button
            className="create-btn w-full bg-primary text-white font-medium py-2.5 px-4 rounded-lg flex items-center justify-center mb-4 hover:transform hover:-translate-y-0.5 hover:shadow-lg transition-all"
            onClick={handleCreateNewList}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Create New List
          </button>
        </div>

        <div className="add-to-list-modal-footer">
          <button className="modal-btn modal-btn-inactive" onClick={onClose}>
            Cancel
          </button>
          <button
            className="modal-btn modal-btn-active"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Add NewListModal */}
      <NewListModal
        isOpen={isNewListModalOpen}
        onClose={handleCloseNewListModal}
        onCreate={handleCreateList}
        userData={userData}
        apiClient={apiClient}
        scrollContainerRef={{ current: document.body }} // Pass document.body as the scroll container
      />
    </div>
  );
}

export default AddToListModal;